// 首先导入 polyfills
// 根据当前项目的 react-router 版本引入对应的 NuqsAdapter
import { Suspense, lazy, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';

import { ConfigProvider as ArcoDesignConfigProvider, Spin } from '@arco-design/web-react';
import enUS from '@arco-design/web-react/es/locale/en-US';
import '@arco-themes/react-hanbo/index.less';
import moment from 'moment-mini';
import { NuqsAdapter } from 'nuqs/adapters/react';

import '@/delivery-type-plugin/bootstrap';
import { usePlatformConfig } from '@/hooks';
import { useTerminalWindowStore } from '@/pages/clusters/commponets/container-terminal/terminal-window-store';

import { ConfigProvider } from './components/ConfigProvider';
import './global.css';
import './i18n';
import './polyfills';
import { router } from './routes';
import { useResourceErrorHandler } from './routes/useResourceErrHandler';
import './styles/animate.css';
import './styles/common.less';
import './styles/normalize.css';

moment.suppressDeprecationWarnings = true;
const TerminalFloatingWindow = lazy(() => import('@/pages/clusters/commponets/container-terminal/TerminalFloatingWindow'));

type LangType = 'en' | 'zh-cn';

function App() {
  useResourceErrorHandler();
  const { isLoading: platformConfigLoading } = usePlatformConfig();
  const [lang, setLang] = useState<LangType>('zh-cn');
  const { i18n } = useTranslation();
  const terminalVisible = useTerminalWindowStore((state) => state.visible);

  // 从 localStorage 中获取语言设置
  useEffect(() => {
    const l = localStorage.getItem('lang');
    console.log('cache-lang', l);
    // 缓存兼容
    if (l === 'zh_cn') {
      setLang('zh-cn');
      return;
    }
    if (l === 'en_us') {
      setLang('en');
      return;
    }
    setLang((l as LangType) || 'zh-cn');
  }, []);

  // 设置 i18n 的语言
  useEffect(() => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
  }, [lang, i18n]);

  // Arco Design 国际化 默认就是中文,不用再重复导入了
  const ArcoLocale = lang === 'en' ? enUS : undefined;

  if (platformConfigLoading) {
    return <Spin block size={40} style={{ display: 'flex', marginTop: '40vh', justifyContent: 'center' }} />;
  }

  return (
    <ArcoDesignConfigProvider locale={ArcoLocale}>
      <NuqsAdapter>
        <ConfigProvider lang={lang} setLang={setLang}>
          <>
            <RouterProvider router={router} />
            {terminalVisible ? (
              <Suspense fallback={null}>
                <TerminalFloatingWindow />
              </Suspense>
            ) : null}
          </>
        </ConfigProvider>
      </NuqsAdapter>
    </ArcoDesignConfigProvider>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Root element with id 'root' not found");
}
const root = createRoot(rootElement);
root.render(<App />);
